// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Button, Group, Stack, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createReference } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import { IconSearch } from '@tabler/icons-react';
import type { DataTableSortStatus } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import { type JSX, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { TableAnchor } from '../../components/layout/TableAnchor';
import { TableBadge } from '../../components/TableBadge';
import { useRole } from '../../hooks/useRole';
import { botIdentifier } from '../../lib/bot-identifier';
import { showErrorNotification } from '../../utils/notifications';
import { hasPermission } from '../../utils/permissions';
import { CancelInviteModal } from './CancelInviteModal';
import { ChangeRoleModal } from './ChangeRoleModal';
import { DisableProviderModal } from './DisableProviderModal';
import { EnableProviderModal } from './EnableProviderModal';
import { DEFAULT_PAGE_SIZE } from './staffing-constants';
import { EMPTY_VALUE, formatLicenseStates, getRoleCode, type StaffRow } from './staffing-utils';

interface StaffTableProps {
  rows: StaffRow[];
  total: number;
  loading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  sortStatus: DataTableSortStatus<StaffRow>;
  onSortStatusChange: (status: DataTableSortStatus<StaffRow>) => void;
  search: string;
  onSearchChange: (value: string) => void;
  onRefresh?: () => void;
  onReload?: () => void;
  /** When false, hides all write affordances (Add New button + entire Actions column). */
  canMutate?: boolean;
}

export function StaffTable({
  rows,
  total,
  loading,
  page,
  onPageChange,
  sortStatus,
  onSortStatusChange,
  search,
  onSearchChange,
  onRefresh,
  onReload,
  canMutate = true,
}: StaffTableProps): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { role, isAdmin, loading: roleLoading } = useRole();
  const [resendingIds, setResendingIds] = useState<Set<string>>(new Set());
  const [cancelModalRow, setCancelModalRow] = useState<StaffRow | null>(null);
  const [disableModalRow, setDisableModalRow] = useState<StaffRow | null>(null);
  const [enableModalRow, setEnableModalRow] = useState<StaffRow | null>(null);
  const [changeRoleModalRow, setChangeRoleModalRow] = useState<StaffRow | null>(null);

  // Defer until role is resolved so the Actions column does not flash hidden→visible.
  // Also gated by canMutate so RCM (canMutate=false) never sees action buttons.
  const canManageInvites = canMutate && !roleLoading && hasPermission({ role, isAdmin, permission: 'manage-staff' });
  const canChangeRole = canMutate && !roleLoading && isAdmin;

  const handleResendInvite = useCallback(
    async (row: StaffRow) => {
      const practitionerId = row.practitioner.id;
      if (!practitionerId) {
        return;
      }
      setResendingIds((prev) => new Set(prev).add(practitionerId));
      try {
        await medplum.executeBot(botIdentifier('resend-provider-invite'), {
          practitionerReference: createReference(row.practitioner),
        });
        notifications.show({
          color: 'green',
          title: 'Invite resent',
          message: `A new invite email has been sent to ${row.email}.`,
        });
        onRefresh?.();
      } catch (err) {
        showErrorNotification(err);
      } finally {
        setResendingIds((prev) => {
          const next = new Set(prev);
          next.delete(practitionerId);
          return next;
        });
      }
    },
    [medplum, onRefresh]
  );

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" wrap="wrap">
        <Title order={3}>Staff</Title>
        <Group gap="sm" wrap="nowrap">
          <TextInput
            placeholder="Search"
            leftSection={<IconSearch size={14} />}
            size="sm"
            w={220}
            value={search}
            onChange={(e) => onSearchChange(e.currentTarget.value)}
            data-testid="staffing-search"
          />
          {canMutate && (
            <Button
              onClick={() => {
                navigate('/invite-provider')?.catch(console.error);
              }}
              data-testid="staffing-add-new"
            >
              Add New
            </Button>
          )}
        </Group>
      </Group>

      <DataTable
        fetching={loading}
        minHeight={200}
        records={rows}
        idAccessor={(row) => row.practitioner.id ?? row.email}
        striped
        highlightOnHover
        fz="sm"
        noRecordsText={total === 0 && !loading ? 'No staff yet.' : 'No matches.'}
        totalRecords={total}
        recordsPerPage={DEFAULT_PAGE_SIZE}
        page={page}
        onPageChange={onPageChange}
        sortStatus={sortStatus}
        onSortStatusChange={onSortStatusChange}
        columns={[
          {
            accessor: 'name',
            title: 'Name',
            sortable: true,
            render: (row: StaffRow) => (
              <TableAnchor
                component="button"
                type="button"
                onClick={() => {
                  if (row.practitioner.id) {
                    navigate(`/admin/staffing/${row.practitioner.id}`)?.catch(console.error);
                  }
                }}
              >
                {row.name}
              </TableAnchor>
            ),
          },
          {
            accessor: 'status',
            title: 'Status',
            render: (row: StaffRow) => (
              <TableBadge color={row.status === 'Active' ? 'green' : 'gray'} variant="light">
                {row.status}
              </TableBadge>
            ),
          },
          { accessor: 'accountType', title: 'Account Type' },
          { accessor: 'email', title: 'Email' },
          { accessor: 'npi', title: 'NPI' },
          {
            accessor: 'licenses',
            title: 'Licenses',
            render: (row: StaffRow) => formatLicenseStates(row.licenseStates) || EMPTY_VALUE,
          },
          {
            accessor: 'actions',
            title: 'Actions',
            hidden: !canManageInvites,
            render: (row: StaffRow) => {
              const practitionerId = row.practitioner.id ?? '';
              const isResending = resendingIds.has(practitionerId);

              if (row.isOnboardingPending) {
                return (
                  <Group gap="xs" wrap="nowrap">
                    <Button
                      size="xs"
                      variant="light"
                      color="blue"
                      loading={isResending}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResendInvite(row).catch(console.error);
                      }}
                      data-testid={`resend-invite-${practitionerId}`}
                    >
                      Resend invite
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCancelModalRow(row);
                      }}
                      data-testid={`cancel-invite-${practitionerId}`}
                    >
                      Cancel invite
                    </Button>
                    {canChangeRole && (
                      <Button
                        size="xs"
                        variant="light"
                        onClick={(e) => {
                          e.stopPropagation();
                          setChangeRoleModalRow(row);
                        }}
                        data-testid={`change-role-${practitionerId}`}
                      >
                        Change role
                      </Button>
                    )}
                  </Group>
                );
              }

              return (
                <Group gap="xs" wrap="nowrap">
                  {canChangeRole && (
                    <Button
                      size="xs"
                      variant="light"
                      onClick={(e) => {
                        e.stopPropagation();
                        setChangeRoleModalRow(row);
                      }}
                      data-testid={`change-role-${practitionerId}`}
                    >
                      Change role
                    </Button>
                  )}
                  {row.isActive && (
                    <Button
                      size="xs"
                      variant="outline"
                      color="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDisableModalRow(row);
                      }}
                      data-testid={`disable-provider-${practitionerId}`}
                    >
                      Disable
                    </Button>
                  )}
                  {!row.isActive && (
                    <Button
                      size="xs"
                      variant="outline"
                      color="green"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEnableModalRow(row);
                      }}
                      data-testid={`enable-provider-${practitionerId}`}
                    >
                      Enable
                    </Button>
                  )}
                </Group>
              );
            },
          },
        ]}
      />

      {cancelModalRow && (
        <CancelInviteModal
          opened={cancelModalRow !== null}
          onClose={() => setCancelModalRow(null)}
          practitioner={{
            id: cancelModalRow.practitioner.id ?? '',
            name: cancelModalRow.name,
            email: cancelModalRow.email,
          }}
          onSuccess={() => {
            setCancelModalRow(null);
            onReload?.();
          }}
        />
      )}

      {disableModalRow && (
        <DisableProviderModal
          opened={disableModalRow !== null}
          onClose={() => setDisableModalRow(null)}
          practitioner={{
            id: disableModalRow.practitioner.id ?? '',
            name: disableModalRow.name,
            email: disableModalRow.email,
          }}
          onSuccess={() => {
            setDisableModalRow(null);
            onReload?.();
          }}
        />
      )}

      {enableModalRow && (
        <EnableProviderModal
          opened={enableModalRow !== null}
          onClose={() => setEnableModalRow(null)}
          practitioner={{
            id: enableModalRow.practitioner.id ?? '',
            name: enableModalRow.name,
            email: enableModalRow.email,
          }}
          onSuccess={() => {
            setEnableModalRow(null);
            onReload?.();
          }}
        />
      )}

      {changeRoleModalRow && (
        <ChangeRoleModal
          opened={changeRoleModalRow !== null}
          onClose={() => setChangeRoleModalRow(null)}
          practitioner={{
            id: changeRoleModalRow.practitioner.id ?? '',
            name: changeRoleModalRow.name,
            currentRole: getRoleCode(changeRoleModalRow.practitionerRole),
          }}
          onSuccess={() => {
            setChangeRoleModalRow(null);
            onReload?.();
          }}
        />
      )}
    </Stack>
  );
}
